import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CSpinner,
  CAlert
} from '@coreui/react';
import './MainWindowObjects.scss';
import ObjectInfoTab from './TabsMainWinObj/ObjectInfoTab.jsx';
import InvestigationJustificationTab from './TabsMainWinObj/InvestigationJustificationTab.jsx';
import SpecialistsInfoTab from './TabsMainWinObj/SpecialistsInfoTab.jsx';
import MeasuringInstrumentsTab from './TabsMainWinObj/MeasuringInstrumentsTab.jsx';
import OrganizationInfoTab from './TabsMainWinObj/OrganizationInfoTab.jsx';
import WorkProgramTab from './TabsMainWinObj/WorkProgramTab.jsx';
import ClimateParametersTab from './TabsMainWinObj/ClimateParametersTab.jsx';
import ExplanatoryNoteTab from './TabsMainWinObj/ExplanatoryNoteTab.jsx';
import axios from 'axios';

const modalStyle = {
  maxHeight: '97vh',
  maxWidth: '120vw',
  display: 'flex',
  flexDirection: 'column'
};

const tabContentStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto'
};

// Вспомогательная функция для извлечения года из регистрационного номера
const extractYearFromRegNumber = (regNumber) => {
  if (!regNumber) return new Date().getFullYear().toString();
  
  // Ищем паттерны года
  const yearMatch = regNumber.match(/\b(20\d{2})\b/);
  if (yearMatch) return yearMatch[1];
  
  const dotMatch = regNumber.match(/\.(\d{4})\./);
  if (dotMatch) return dotMatch[1];
  
  return new Date().getFullYear().toString();
};

const MainWindowObjects = ({ 
  visible, 
  onClose, 
  availableObjectTypes, 
  selectedObjectType, 
  customerId, 
  onObjectAdded 
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const objectInfoTabRef = useRef();
  const investigationJustificationTabRef = useRef();
  const specialistsInfoTabRef = useRef();
  const measuringInstrumentsTabRef = useRef();
  const organizationInfoTabRef = useRef();
  const workProgramTabRef = useRef();
  const climateParametersTabRef = useRef();
  const explanatoryNoteTabRef = useRef();

  const tabs = [
    { id: '1', label: 'Сведения об объекте' },
    // { id: '2', label: 'Основание выполнения работ' },
    // { id: '3', label: 'Сведения о специалистах' },
    // { id: '4', label: 'Средства измерения и контроля' },
    // { id: '5', label: 'Сведения об организации, проводившей обследования' },
    // { id: '6', label: 'Программа работ' },
    // { id: '7', label: 'Климатические параметры' },
    // { id: '8', label: 'Состав пояснительной записки' }
  ];

  const topTabs = tabs.slice(0, 4);
  const bottomTabs = tabs.slice(4, 8);

  // Мемоизируем функцию сохранения
const handleSaveAll = useCallback(async () => {
    setIsSaving(true);
    setSaveStatus({ type: '', message: '' });

    try {
      const allData = await getAllTabData();
      
      // ОБЪЯВЛЯЕМ ТОКЕН (чтобы не было ошибки ESLint)
      const token = localStorage.getItem("token");

      // ЛОГИКА АВТОМАТИЧЕСКОГО УНИКАЛЬНОГО НАЗВАНИЯ
      if (!allData.Name || !allData.Name.trim()) {
        const typeLabel = selectedObjectType?.ObjectName || "Объект";
        // Генерируем временную метку (часы-минуты-секунды)
        const timePart = new Date().toLocaleTimeString('ru-RU').replace(/:/g, '');
        const datePart = new Date().toLocaleDateString('ru-RU');
        
        // Результат будет: "Здание - 25.05.2024 (143005)"
        allData.Name = `${typeLabel} - ${datePart} (${timePart})`;
        
        if (!allData.ObjName) {
            allData.ObjName = allData.Name;
        }
      }

      if (!allData.idCustomer) {
        throw new Error('Не указан заказчик');
      }

      if (!allData.LDoc) {
        throw new Error('Регистрационный номер обязателен');
      }

      if (!allData.WorkName) {
        throw new Error('Наименование работы обязательно');
      }

      // 2. Создаем структуру папок перед сохранением объекта
      const folderResult = await createFolderStructure(
        allData.idCustomer,
        allData.Name, // Используем Name (objectIdValue) для создания папок
        allData.WorkDir,
        allData.LDoc
      );

      // 3. Обновляем WorkDir с фактически созданным путем
      // Проверяем, что folderResult.createdWorkDir существует
      let finalWorkDir;
      if (folderResult.createdWorkDir) {
        finalWorkDir = folderResult.createdWorkDir;
      } else if (allData.WorkDir) {
        finalWorkDir = allData.WorkDir;
      } else {
        // Создаем fallback путь
        const year = extractYearFromRegNumber(allData.LDoc) || new Date().getFullYear().toString();
        finalWorkDir = `\\\\192.168.0.2\\Data\\Текущие объекты ВЕБ\\Заказчик_${allData.idCustomer}\\${year}\\${allData.Name}`;
      }

      if (!folderResult.success) {
        setSaveStatus({ 
          type: 'warning', 
          message: `Объект будет сохранен, но папки не созданы: ${folderResult.message}`
        });
      }

      // 4. Формируем объект для отправки
      const saveData = {
        idCustomer: allData.idCustomer,
        Name: allData.Name, // ID объекта (objectIdValue)
        Cipher: allData.Cipher,
        Voltage: allData.Voltage,
        LType: allData.LType, // ObjectCType
        LDoc: allData.LDoc,
        Mark: allData.Mark,
        PageNumber: allData.PageNumber,
        Address: allData.Address,
        Year: allData.Year,
        ResLevel: allData.ResLevel,
        SurvReason: allData.SurvReason,
        idSurvOrg: 0, // Всегда 3
        NTypeRating: allData.NTypeRating,
        NTypeObj: allData.NTypeObj, // ObjectType
        idSpecWork: allData.idSpecWork,
        idSpecCheck: allData.idSpecCheck,
        idSpecControl: allData.idSpecControl,
        WorkName: allData.WorkName, // Наименование работы
        BookName: allData.BookName,
        Height: allData.Height,
        NTypeSubObj: allData.NTypeSubObj,
        idRegion: allData.idRegion,
        idOrgName: allData.idOrgName,
        TypeOwn: allData.TypeOwn,
        StateObj: allData.StateObj,
        BuildYearObj: allData.BuildYearObj,
        ProjectTypeObj: allData.ProjectTypeObj,
        RepairYearObj: allData.RepairYearObj,
        idTargetTask: allData.idTargetTask,
        DateSurv: allData.DateSurv,
        idState: allData.idState,
        idOwner: allData.idOwner,
        idDesignSchema: allData.idDesignSchema,
        idDocType: allData.idDocType,
        ObjName: allData.ObjName, // Название объекта
        idOrgStroy: allData.idOrgStroy,
        WorkDir: finalWorkDir, // Используем путь, который содержит objectIdValue
        idSpecWorkSheet: allData.idSpecWorkSheet,
        idSpecWorkPhoto: allData.idSpecWorkPhoto,
        idSpecBuild: allData.idSpecBuild,
        idSpecTech: allData.idSpecTech,
        idSpecJournal: allData.idSpecJournal,
        BaseLinkObj: allData.BaseLinkObj,
        EqInstall: allData.EqInstall,
        HeightText: allData.HeightText,
        InputExpDate: allData.InputExpDate,
        IsDocAnalyseNot2: allData.IsDocAnalyseNot2,
        CoordLat: allData.CoordLat,
        CoordLng: allData.CoordLng,
        DegLat: allData.DegLat,
        DegLng: allData.DegLng,
        idBuildOrg: allData.idBuildOrg,
        PeriodTone: allData.PeriodTone,
        ConditionOper: allData.ConditionOper,
        IsMeasuringVerifyOutToReport: allData.IsMeasuringVerifyOutToReport,
        CustomOrgName: allData.CustomOrgName,
        idStateConclusion: allData.idStateConclusion,
        mSortPlanGraph: allData.mSortPlanGraph,
        IsDateSurv: allData.IsDateSurv,
        DateSignConclusion: allData.DateSignConclusion,
        IsNotOutCategory: allData.IsNotOutCategory,
        idSigner1: allData.idSigner1,
        idSigner2: allData.idSigner2,
        ObjIdIndex: allData.ObjIdIndex,
        LandImage: allData.LandImage,
        isLanding: allData.isLanding,
        LandName: allData.LandName,
        LHPoint: allData.LHPoint,
        RDPoint: allData.RDPoint,
        IsPreCategory: allData.IsPreCategory,
        FloorsNum: allData.FloorsNum,
        DateSurvNext: allData.DateSurvNext,
        TreeGroupName: allData.TreeGroupName,
        isDelete: allData.isDelete,
        ConstructiveScheme: allData.ConstructiveScheme
      };

      console.log('Данные для отправки на сервер:', saveData);

      // 5. Сохраняем объект в БД
      const response = await axios.post('/api/objects/line-list/', saveData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 201 || response.status === 200) {
        const successMessage = folderResult.success 
          ? 'Объект успешно сохранен и структура папок создана'
          : 'Объект успешно сохранен, но возникли проблемы с созданием папок';
        
        setSaveStatus({ 
          type: 'success', 
          message: successMessage 
        });

        // Даем время показать сообщение
        setTimeout(() => {
          if (onObjectAdded) {
            onObjectAdded();
          }
          onClose();
        }, 1500);
        
      } else {
        throw new Error(response.data?.message || 'Неизвестная ошибка при сохранении');
      }

    } catch (error) {
      let errorMessage = 'Ошибка сохранения объекта';
      
      if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Неизвестная ошибка';
        console.error('Ошибка сервера:', error.response.data);
      } else if (error.request) {
        errorMessage = 'Нет ответа от сервера';
      } else {
        errorMessage = error.message;
      }
      
      setSaveStatus({ 
        type: 'danger', 
        message: errorMessage 
      });
    } finally {
      setIsSaving(false);
    }
  }, [customerId, onObjectAdded, onClose]);

  // Обработчик нажатия клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Проверяем, что модальное окно открыто
      if (!visible) return;
      
      // Получаем активный элемент
      const activeElement = document.activeElement;
      
      // Проверяем, что нажата клавиша Enter
      if (e.key === 'Enter' && !isSaving) {
        // Проверяем, является ли активный элемент текстовым полем
        const isInputField = 
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT';
        
        // Если это поле ввода и НЕ textarea, сохраняем
        if (isInputField && activeElement.type !== 'textarea') {
          e.preventDefault(); // Предотвращаем стандартное поведение
          handleSaveAll();
        } 
        // Если это не поле ввода, сохраняем
        else if (!isInputField) {
          e.preventDefault();
          handleSaveAll();
        }
        // Если это textarea, НЕ сохраняем - позволяем перенос строк
      }
      
      // Обработка Escape для закрытия
      if (e.key === 'Escape' && !isSaving) {
        e.preventDefault();
        onClose();
      }
    };

    // Добавляем обработчик события
    document.addEventListener('keydown', handleKeyDown);
    
    // Очищаем обработчик при размонтировании
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, isSaving, onClose, handleSaveAll]);

  const getInitialObjectType = () => {
    return selectedObjectType ? selectedObjectType.ObjectID.toString() : '';
  };

  // ID объекта всегда "Новый объект" при создании
const getInitialObjectId = () => {
  return ''; 
};

  // Собираем данные со всех вкладок
  const getAllTabData = async () => {
    try {
      let allData = {
        idCustomer: customerId ? Number(customerId) : 0,
        Name: 'Новый объект', // ID объекта
        isDelete: null
      };

      // Данные из первой вкладки (ObjectInfoTab)
      if (objectInfoTabRef.current?.getFormData) {
        const objectInfoData = objectInfoTabRef.current.getFormData();
        
        // Копируем ВСЕ поля из objectInfoData в allData
        if (objectInfoData) {
          allData = { ...allData, ...objectInfoData };
        }
      }

      return allData;
    } catch (error) {
      return {
        idCustomer: customerId ? Number(customerId) : 0,
        Name: 'Новый объект',
        ObjName: 'Новый объект',
        isDelete: null
      };
    }
  };

  // Функция для создания структуры папок
  const createFolderStructure = async (customerId, objectId, workDirPath, regNumber) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error('Отсутствует токен авторизации');
      }

      const year = extractYearFromRegNumber(regNumber);

      // Создаем структуру папок через API
      const response = await axios.post('/api/objects/line-list/customer-workdir/', {
        customer_id: customerId,
        year: year,
        object_name: objectId, // ИСПОЛЬЗУЕМ objectId (objectIdValue из ObjectInfoTab)
        create_structure: true,
        standard_folders: ["Фото", "Видео", "Документация", "Итоговые документы", "Расчёт"]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Получаем фактически созданный путь от сервера
      const createdWorkDir = response.data?.created_path || response.data?.workdir;
      
      if (response.data?.overall_status === 'success' || response.data?.overall_status === 'partial_success') {
        return {
          success: true,
          message: 'Структура папок создана успешно',
          data: response.data,
          createdWorkDir: createdWorkDir || null // Возвращаем null если пути нет
        };
      } else {
        throw new Error(response.data?.message || 'Ошибка создания структуры папок');
      }

    } catch (error) {
      // Fallback: создаем путь вручную с использованием objectId
      const year = extractYearFromRegNumber(regNumber) || new Date().getFullYear().toString();
      const fallbackPath = `\\\\192.168.0.2\\Data\\Текущие объекты ВЕБ\\Заказчик_${customerId}\\${year}\\${objectId}`;
      
      return {
        success: false,
        message: error.response?.data?.error || error.message || 'Ошибка создания папок',
        createdWorkDir: fallbackPath // Возвращаем fallback путь
      };
    }
  };

  // Обработчик для сохранения текущей вкладки
  const handleTabSave = async () => {
    switch (activeTab) {
      case '1':
        if (objectInfoTabRef.current?.save) {
          await objectInfoTabRef.current.save();
        }
        break;
      case '2':
        if (investigationJustificationTabRef.current?.save) {
          await investigationJustificationTabRef.current.save();
        }
        break;
      case '3':
        if (specialistsInfoTabRef.current?.save) {
          await specialistsInfoTabRef.current.save();
        }
        break;
      case '4':
        if (measuringInstrumentsTabRef.current?.save) {
          await measuringInstrumentsTabRef.current.save();
        }
        break;
      case '5':
        if (organizationInfoTabRef.current?.save) {
          await organizationInfoTabRef.current.save();
        }
        break;
      case '6':
        if (workProgramTabRef.current?.save) {
          await workProgramTabRef.current.save();
        }
        break;
      case '7':
        if (climateParametersTabRef.current?.save) {
          await climateParametersTabRef.current.save();
        }
        break;
      case '8':
        if (explanatoryNoteTabRef.current?.save) {
          await explanatoryNoteTabRef.current.save();
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <CModal 
        alignment="start" 
        visible={visible} 
        onClose={() => {
          if (!visible || isSaving) return;
          onClose();
        }}
        className="main-window-objects fixed-size"
        size="xl"
        backdrop="static"
        fullscreen="lg"
        style={modalStyle}
        onKeyDown={(e) => {
          // Обработка нажатия Enter на уровне модального окна
          if (e.key === 'Enter' && !isSaving) {
            e.preventDefault();
            handleSaveAll();
          }
        }}
      >
        <CModalHeader>
          <CModalTitle>
            {selectedObjectType 
              ? `Редактирование объекта: ${selectedObjectType.ObjectName}` 
              : 'Добавить новый объект'
            }
          </CModalTitle>
        </CModalHeader>
        
        <CModalBody 
          className="p-0" 
          style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
          // Добавляем обработчик на тело модального окна
          onKeyDown={(e) => {
            // Предотвращаем стандартное поведение формы при нажатии Enter
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
        >
          {/* Статус сохранения */}
          {saveStatus.message && (
            <div className="p-3">
              <CAlert 
                color={saveStatus.type || 'info'}
                onClose={() => setSaveStatus({ type: '', message: '' })}
                dismissible
              >
                {saveStatus.message}
              </CAlert>
            </div>
          )}

          <div className="tabs-container">
            <CNav variant="tabs" className="main-tabs-list top-tabs">
              {topTabs.map((tab) => (
                <CNavItem key={tab.id} className={`main-tab-item ${tab.id === activeTab ? 'active' : ''}`}>
                  <CNavLink
                    href="#"
                    active={tab.id === activeTab}
                    className="main-tab"
                    // ПРАВКА: Блокируем вкладку, если это не ID 1
                    disabled={tab.id !== '1'}
                    onClick={(e) => {
                      e.preventDefault();
                      // ПРАВКА: Переходим на вкладку только если это ID 1
                      if (tab.id === '1') {
                        setActiveTab(tab.id);
                      }
                    }}
                  >
                    {tab.label}
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
            
            <CNav variant="tabs" className="main-tabs-list bottom-tabs">
              {bottomTabs.map((tab) => (
                <CNavItem key={tab.id} className={`main-tab-item ${tab.id === activeTab ? 'active' : ''}`}>
                  <CNavLink
                    href="#"
                    active={tab.id === activeTab}
                    className="main-tab"
                    // ПРАВКА: Всегда блокируем нижние вкладки
                    disabled={true}
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {tab.label}
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
          </div>
          
          <CTabContent className="main-tab-content p-0" style={tabContentStyle}>
            {/* Вкладка 1: Сведения об объекте */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '1'}>
              <ObjectInfoTab 
                ref={objectInfoTabRef}
                initialObjectId={getInitialObjectId()} // Всегда "Новый объект"
                initialObjectType={getInitialObjectType()}
                customerId={customerId}
                availableObjectTypes={availableObjectTypes}
                selectedObjectTypeFromModal={selectedObjectType} // Передаем выбранный тип объекта
                onSave={() => {}}
                onEnterPress={handleSaveAll} // Добавляем проп для обработки Enter
              />
            </CTabPane>
            
            {/* Вкладка 2: Основание выполнения работ */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '2'}>
              <InvestigationJustificationTab 
                ref={investigationJustificationTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 3: Сведения о специалистах */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '3'}>
              <SpecialistsInfoTab 
                ref={specialistsInfoTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 4: Средства измерения и контроля */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '4'}>
              <MeasuringInstrumentsTab 
                ref={measuringInstrumentsTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 5: Сведения об организации, проводившей обследования */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '5'}>
              <OrganizationInfoTab 
                ref={organizationInfoTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 6: Программа работ */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '6'}>
              <WorkProgramTab 
                ref={workProgramTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 7: Климатические параметры */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '7'}>
              <ClimateParametersTab 
                ref={climateParametersTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
            
            {/* Вкладка 8: Состав пояснительной записки */}
            <CTabPane className="main-tab-panel p-0 fixed-tab-content" visible={activeTab === '8'}>
              <ExplanatoryNoteTab 
                ref={explanatoryNoteTabRef}
                initialData={null}
                onSave={() => {}}
              />
            </CTabPane>
          </CTabContent>
        </CModalBody>
        
        <CModalFooter className="main-window-footer">
          <div className="footer-left">
            <span className="text-danger small ms-3">*обязательные поля для заполнения</span>
          </div>
          
          <div className="footer-right">
            {isSaving && <CSpinner size="sm" className="me-2" />}
            <CButton 
              color="primary" 
              className="me-2" 
              onClick={handleSaveAll}
              disabled={isSaving}
            >
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </CButton>
            <CButton 
              color="secondary" 
              onClick={onClose}
              disabled={isSaving}
            >
              Отмена
            </CButton>
          </div>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default MainWindowObjects;